import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useHeatmapData } from '../hooks/useHeatmapData';

// ---------------------------------------------------------------------------
// Helpers (mirrors ActivityHeatmap.tsx)
// ---------------------------------------------------------------------------

function toDateID(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}

function addDays(base: Date, days: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  d.setHours(0, 0, 0, 0);
  return d;
}

// ---------------------------------------------------------------------------

const HeatmapFullYear: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId') || '';
  const currentYear = new Date().getFullYear();

  // Single source of truth — dateID map
  const { intensityMap, loading, error } = useHeatmapData(userId);

  const getColor = (intensity: number): string => {
    switch (intensity) {
      case 0: return '#e5e7eb';
      case 1: return '#cce7f0';
      case 2: return '#66c3db';
      case 3: return '#33afe3';
      case 4: return '#0096C7';
      default: return '#e5e7eb';
    }
  };

  // ── Stats: iterate calendar days Jan 1 → today via dateID lookup ───────────
  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yearStart = new Date(currentYear, 0, 1);
    const dayOfYear =
      Math.floor((today.getTime() - yearStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    let daysWithActivity = 0;
    let longestStreak = 0;
    let tempStreak = 0;

    const dailyIntensities: number[] = [];
    for (let offset = 0; offset < dayOfYear; offset++) {
      dailyIntensities.push(intensityMap.get(toDateID(addDays(yearStart, offset))) ?? 0);
    }

    for (const intensity of dailyIntensities) {
      if (intensity > 0) {
        daysWithActivity++;
        tempStreak++;
        longestStreak = Math.max(longestStreak, tempStreak);
      } else {
        tempStreak = 0;
      }
    }

    // Current streak: walk backward from today
    let currentStreak = 0;
    for (let i = dailyIntensities.length - 1; i >= 0; i--) {
      if (dailyIntensities[i] > 0) currentStreak++;
      else break;
    }

    const percentage = Math.round((daysWithActivity / dayOfYear) * 100);

    return {
      total: 365,
      completed: daysWithActivity,
      percentage,
      longestStreak,
      currentStreak,
      dayOfYear,
    };
  }, [intensityMap, currentYear]);

  // ── Calendar grid: one entry per month ────────────────────────────────────
  const yearCalendar = useMemo(() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return months.map((monthName, monthIndex) => {
      const firstDay = new Date(currentYear, monthIndex, 1);
      const lastDay = new Date(currentYear, monthIndex + 1, 0);
      const startDayOfWeek = firstDay.getDay(); // 0 = Sun

      const weeks: Array<Array<{ date: Date | null; intensity: number; isFuture: boolean }>> = [];
      let currentWeek: Array<{ date: Date | null; intensity: number; isFuture: boolean }> = [];

      // Leading empty cells
      for (let i = 0; i < startDayOfWeek; i++) {
        currentWeek.push({ date: null, intensity: 0, isFuture: false });
      }

      // Actual days — direct dateID lookup
      for (let day = 1; day <= lastDay.getDate(); day++) {
        const date = new Date(currentYear, monthIndex, day);
        date.setHours(0, 0, 0, 0);
        const isFuture = date > today;
        const intensity = isFuture ? 0 : (intensityMap.get(toDateID(date)) ?? 0);

        currentWeek.push({ date, intensity, isFuture });

        if (currentWeek.length === 7) {
          weeks.push(currentWeek);
          currentWeek = [];
        }
      }

      // Trailing cells to complete last row
      if (currentWeek.length > 0) {
        while (currentWeek.length < 7) {
          currentWeek.push({ date: null, intensity: 0, isFuture: false });
        }
        weeks.push(currentWeek);
      }

      return { name: monthName, monthIndex, weeks };
    });
  }, [intensityMap, currentYear]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-48 mb-8"></div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">Error loading activity data</p>
          <button onClick={() => navigate(-1)} className="text-[#0096C7] hover:underline">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        {/* Title & Stats */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Activity</h1>
          <p className="text-base md:text-lg text-[#0096C7] font-medium mb-4">{currentYear}</p>

          <div className="flex flex-wrap items-center gap-3 text-sm md:text-base text-gray-600">
            <span className="font-semibold text-gray-900">
              {stats.completed} of {stats.dayOfYear} days
            </span>
            <span className="text-gray-400">•</span>
            <span className="font-semibold text-gray-900">{stats.percentage}%</span>
            {stats.currentStreak > 0 && (
              <>
                <span className="text-gray-400">•</span>
                <span>🔥 {stats.currentStreak} {stats.currentStreak === 1 ? 'day' : 'days'} streak</span>
              </>
            )}
            {stats.longestStreak > 0 && (
              <>
                <span className="text-gray-400">•</span>
                <span>
                  {stats.longestStreak} {stats.longestStreak === 1 ? 'day' : 'days'} longest streak
                </span>
              </>
            )}
          </div>
        </div>

        {/* Month Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-6">
          {yearCalendar.map((month) => (
            <div key={month.monthIndex} className="bg-white">
              <h3 className="text-base md:text-lg font-semibold mb-3 text-gray-900">
                {month.name}
              </h3>
              <div className="space-y-1">
                {month.weeks.map((week, weekIdx) => (
                  <div key={weekIdx} className="flex gap-1">
                    {week.map((day, dayIdx) => (
                      <div
                        key={`${weekIdx}-${dayIdx}`}
                        className="flex-1 aspect-square rounded-sm transition-all duration-150"
                        style={{
                          backgroundColor: day.date
                            ? (day.isFuture ? '#e5e7eb' : getColor(day.intensity))
                            : 'transparent',
                          opacity: day.date ? (day.isFuture ? 0.3 : 1) : 0,
                          minWidth: '8px',
                          maxWidth: '24px',
                        }}
                        title={
                          day.date
                            ? `${day.date.toLocaleDateString()} — ${day.isFuture ? 'Future' : `Intensity: ${day.intensity}`}`
                            : ''
                        }
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="mt-12 flex items-center justify-center gap-3 text-sm text-gray-500">
          <span>Less</span>
          <div className="flex gap-1">
            {[0, 1, 2, 3, 4].map((level) => (
              <div key={level} className="w-5 h-5 rounded-sm" style={{ backgroundColor: getColor(level) }} />
            ))}
          </div>
          <span>More</span>
        </div>
      </div>
    </div>
  );
};

export default HeatmapFullYear;
